import '@popperjs/core';
import 'bootstrap';

import {
  DOCUMENT_UPLOAD_MAX_SIZE_MB,
  DOWNLOAD_REQUEST_APPROVAL_WORDING,
  DOWNLOAD_REQUEST_REJECTION_REASON_MAX_LENGTH,
} from '@/shared/constants';

import { alertModal, confirmModal, promptModal } from '@/front/modal';
import { uploadFileToTemp } from '@/front/utils/uploadToTemp';

document.addEventListener('DOMContentLoaded', function () {
  initSignInCodeInputs();

  // Fade out alert
  setTimeout(() => {
    const alert = document.getElementById('alert');
    if (alert) {
      alert.classList.add('fade');
      alert.addEventListener('transitionend', () => alert.remove());
    }
  }, 3000);

  const uploadRequestForm = document.getElementById('uploadRequestForm') as HTMLFormElement | null;

  if (uploadRequestForm) {
    // Delete existing file
    [...uploadRequestForm.querySelectorAll('tr[data-type="upload"]')]
      .map((tr) => tr as HTMLTableRowElement)
      .forEach((tr, i) => {
        const fileInput = tr.querySelector(`input[name="upload${i}"]`) as HTMLInputElement;
        const deleteLink = tr.querySelector('a.delete') as HTMLAnchorElement;

        deleteLink.addEventListener('click', async () => {
          if (await confirmModal('Are you sure you want to delete this file?', 'Delete', 'danger')) {
            fileInput.value = JSON.stringify({ action: 'delete' });
            uploadRequestForm.submit();
          }
        });
      });

    // Upload new file
    const uploadInput = uploadRequestForm.querySelector(`input[name="upload"]`) as HTMLInputElement;
    const fileInput = uploadRequestForm.querySelector<HTMLInputElement>('input[type="file"]')!;

    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      if (file.size > DOCUMENT_UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
        alertModal(`File is too large. Max size is ${DOCUMENT_UPLOAD_MAX_SIZE_MB}MB.`);
        fileInput.value = '';
        return;
      }

      const { tempKey, tempPutUrl } = fileInput.dataset as {
        tempKey: string;
        tempPutUrl: string;
      };

      try {
        const meta = await uploadFileToTemp(file, { tempKey, tempPutUrl });
        uploadInput.value = JSON.stringify(meta);
        uploadRequestForm.submit();
      } catch (err) {
        console.error('Error uploading file:', err);
        alertModal('Error uploading file');
      }
    });

    // Drag and drop (single file)
    uploadRequestForm.addEventListener('dragenter', (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.items.length > 1) {
        e.dataTransfer.dropEffect = 'none';
      }
    });

    uploadRequestForm.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      uploadRequestForm.classList.add('dragover');
    });

    uploadRequestForm.addEventListener('dragleave', () => {
      uploadRequestForm.classList.remove('dragover');
    });

    uploadRequestForm.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      uploadRequestForm.classList.remove('dragover');

      if (!e.dataTransfer) return;

      const files = e.dataTransfer.files;

      if (files.length !== 1) {
        alertModal('Please drop one file at a time');
        return;
      }

      const dt = new DataTransfer();
      dt.items.add(files[0]);
      fileInput.files = dt.files;

      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  const saveDraftButton = document.getElementById('saveDraftButton') as HTMLButtonElement | null;

  if (saveDraftButton) {
    saveDraftButton.addEventListener('click', () => {
      setFormComplete(saveDraftButton, false);
    });
  }

  const submitButton = document.getElementById('submitButton') as HTMLButtonElement | null;

  if (submitButton) {
    submitButton.addEventListener('click', async (e) => {
      await submitFormAfterConfirm(
        e,
        submitButton,
        `Are you sure you want to submit this response? Once submitted, it will be locked and can't be edited.`,
      );
    });
  }

  // Reload when returning from download to show updated checkboxes state
  const downloadTable = document.querySelector('table tr[data-type="download"]');
  if (downloadTable) {
    let pendingReload = false;
    const downloadLinks = document.querySelectorAll('tr[data-type="download"] a[href]');

    downloadLinks.forEach((link) => {
      link.addEventListener('click', () => {
        pendingReload = true;
      });
    });

    window.addEventListener('focus', () => {
      if (pendingReload) {
        pendingReload = false;
        location.reload();
      }
    });
  }

  const downloadApprovalForm = document.getElementById('downloadApprovalForm') as HTMLFormElement | null;

  if (downloadApprovalForm) {
    const decisionInput = document.getElementById('approvalDecision') as HTMLInputElement;
    const rejectionReasonInput = document.getElementById('rejectionReason') as HTMLInputElement;
    const approveButton = document.getElementById('approveButton') as HTMLButtonElement;
    const rejectButton = document.getElementById('rejectButton') as HTMLButtonElement;

    approveButton?.addEventListener('click', async () => {
      if (!(await confirmModal(DOWNLOAD_REQUEST_APPROVAL_WORDING, 'Approve'))) return;
      decisionInput.value = 'approved';
      downloadApprovalForm.submit();
    });

    rejectButton?.addEventListener('click', async () => {
      const reason = await promptModal({
        title: 'Reject request',
        message: 'Please provide a reason:',
        confirmButtonLabel: 'Reject',
        variant: 'danger',
        maxLength: DOWNLOAD_REQUEST_REJECTION_REASON_MAX_LENGTH,
      });
      if (!reason) return;

      decisionInput.value = 'rejected';
      rejectionReasonInput.value = reason;
      downloadApprovalForm.submit();
    });
  }

  document.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-complete-url]').forEach((checkbox) => {
    checkbox.addEventListener('click', async (e) => {
      e.preventDefault();
      if (
        !(await window.confirmModal('Are you sure you want to mark this task as complete?', 'Mark complete', 'warning'))
      )
        return;
      checkbox.checked = true;
      await fetch(checkbox.dataset.completeUrl!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true }),
      });
      location.reload();
    });
  });
});

function initSignInCodeInputs() {
  const codeInputs = document.querySelectorAll<HTMLInputElement>('#code-inputs input');
  const codeInput = document.getElementById('code-input') as HTMLInputElement | null;
  const form = document.getElementById('verify-form') as HTMLFormElement | null;

  if (!codeInputs.length || !codeInput || !form) return;

  const updateCode = () => {
    codeInput.value = Array.from(codeInputs)
      .map((i) => i.value)
      .join('');
  };

  const focusNext = (idx: number) => {
    if (idx < codeInputs.length - 1) codeInputs[idx + 1].focus();
  };

  const focusPrev = (idx: number) => {
    if (idx > 0) codeInputs[idx - 1].focus();
  };

  const fillFromDigits = (digits: string[], startIdx: number) => {
    digits.forEach((d, i) => {
      if (codeInputs[startIdx + i]) codeInputs[startIdx + i].value = d;
    });
    const lastIdx = Math.min(startIdx + digits.length - 1, codeInputs.length - 1);
    codeInputs[lastIdx].focus();
    updateCode();
  };

  codeInputs.forEach((input, idx) => {
    input.addEventListener('paste', function (e) {
      e.preventDefault();
      const text = e.clipboardData?.getData('text') ?? '';
      const digits = text.replace(/\D/g, '').slice(0, codeInputs.length).split('');
      if (digits.length) fillFromDigits(digits, idx);
    });

    input.addEventListener('input', function () {
      const v = this.value.replace(/\D/g, '');
      this.value = v;
      updateCode();
      if (v) focusNext(idx);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Backspace' && !this.value) focusPrev(idx);
    });
  });

  form.addEventListener('submit', function (e) {
    updateCode();
    if (codeInput.value.length !== 6) e.preventDefault();
  });
}

function setFormComplete(button: HTMLButtonElement, complete: boolean) {
  const completeInput = button.form?.querySelector('[name=complete]') as HTMLInputElement | null;

  if (completeInput) {
    completeInput.value = String(complete);
  }
}

async function submitFormAfterConfirm(event: MouseEvent, button: HTMLButtonElement, message: string): Promise<void> {
  event.preventDefault();

  const form = button.form;
  if (!form || !form.reportValidity()) return;

  if (!(await confirmModal(message, 'Submit', 'warning'))) return;

  setFormComplete(button, true);
  form.submit();
}
